export const siteContent = {
  navbar: {
    logo: "Zapify",
    links: [
      { label: "Home",       href: "#hero" },
      { label: "Free Tools", href: "#free-tools" },
      { label: "Blogs",      href: "#blogs" },
      { label: "Contact",    href: "#contact" },
    ],
  },
  hero: {
    headingPrefix: "Reply to ",
    headingEmphasis: "comments",
    headingMid: " with smart DMs - while you keep creating",
    description: "Automate on-brand Instagram DMs that convert - without the inbox overload.",
    ctaPrimary: "Start free",
    ctaSecondary: "Video Walkthrough",
    trustMicrocopy: "No credit card required · Cancel anytime",
  },
  impact: {
    eyebrow: "Our impact",
    titleBefore: "Real engagement. ",
    titleAccent: "Real results.",
    subtitle: "Automation that turns comments into trust—and revenue.",
    heroMetric: {
      label: "DM's Sent Automatically",
      /** Count up to 2M+ */
      start: 1_000_000,
      end: 2_000_000,
      format: "m" as const,
    },
    /** Hero already shows DM volume; three supporting stats only. */
    cards: [
      {
        label: "Followers Gained",
        start: 500_000,
        end: 1_400_000,
        format: "m" as const,
        icon: "users" as const,
        accent: "pink" as const,
      },
      {
        label: "Comments Sent",
        start: 1_000_000,
        end: 2_500_000,
        format: "m" as const,
        icon: "message" as const,
        accent: "orange" as const,
      },
      {
        label: "Countries",
        start: 1,
        end: 31,
        format: "plain" as const,
        icon: "globe" as const,
        accent: "emerald" as const,
      },
    ],
  },
  howItWorks: {
    title: "Automate in minutes, not meetings",
    subtitle: "Three concise steps—mirror how teams actually launch on Zapify.",
    steps: [
      {
        title: "Define triggers",
        description: "Select posts and keyword or emoji patterns that should start a DM sequence.",
      },
      {
        title: "Craft the DM journey",
        description:
          "Blend offers, links, and lead capture in a message flow that still feels human and on-policy.",
      },
      {
        title: "Ship, measure, refine",
        description: "Let automation handle volume; you optimize campaigns from clear performance data.",
      },
    ],
  },
  features: {
    title: "You Create. Zapify Converts.",
    subtitle: "One simple dashboard for comment triggers, AI-powered DMs, lead capture, follow-up, and the team that runs it all.",
    items: [
      {
        title: "DM Automation",
        lines: [
          "Send a DM the second someone comments.",
          "Turn each reaction into a real conversation.",
        ] as const,
        iconKey: "dmAutomation" as const,
        imageLight: "/features/dm_auto_mation_light.webp" as const,
        imageDark: "/features/dm_automation_dark.webp" as const,
      },
      {
        title: "Ask to Follow",
        lines: [
          "Ask people to follow before you send the link.",
          "Prompt at the right moment without manual chasing.",
        ] as const,
        iconKey: "askFollow" as const,
        imageLight: "/features/ask_to_follow_light.webp" as const,
        imageDark: "/features/ask_to_follow_dark.webp" as const,
      },
      {
        title: "AI Replies",
        lines: [
          "Reply in your tone without writing every message.",
          "Keep responses contextual and on-brand.",
        ] as const,
        iconKey: "aiReplies" as const,
        imageLight: "/features/ai_reply_light.webp" as const,
        imageDark: "/features/ai_reply_dark.webp" as const,
      },
      {
        title: "Custom Voice",
        lines: [
          "Set your tone, length, emojis, and rules once.",
          "Keep every campaign consistent.",
        ] as const,
        iconKey: "voice" as const,
        imageLight: "/features/custom_voice_light.webp" as const,
        imageDark: "/features/custom_voice_dark.webp" as const,
      },
      {
        title: "Language Support",
        lines: [
          "Reply across supported languages.",
          "Keep messages clear for more of your audience.",
        ] as const,
        iconKey: "language" as const,
        imageLight: "/features/languages_light.webp" as const,
        imageDark: "/features/languages_dark.webp" as const,
      },
      {
        title: "Collect Emails",
        lines: [
          "Capture emails from conversations.",
          "Move high-intent followers into follow-up.",
        ] as const,
        iconKey: "collectEmail" as const,
        imageLight: "/features/collect_email_light.webp" as const,
        imageDark: "/features/collect_email_dark.webp" as const,
      },
      {
        title: "Team Access",
        lines: [
          "Bring your team in without sharing IG passwords.",
          "Manage automations and workflows in one place.",
        ] as const,
        iconKey: "team" as const,
        imageLight: "/features/team_access_light.webp" as const,
        imageDark: "/features/team_access_dark.webp" as const,
      },
      {
        title: "Multiple Accounts",
        lines: [
          "Run multiple Instagram profiles from one screen.",
          "Built for agencies, brands, and multi-account creators.",
        ] as const,
        iconKey: "multiInstagram" as const,
        imageLight: "/features/multiple_instagram_light.webp" as const,
        imageDark: "/features/multiple_instagram_dark.webp" as const,
      },
    ],
  },
  testimonials: {
    title: "Proof from teams living in the comments",
    subtitle:
      "Operators use Zapify to shrink response time, lift engagement, and carry conversations all the way to sale—without growing headcount linearly.",
    items: [
      {
        name: "Sarah M.",
        role: "Fitness creator · 281K followers",
        quote:
          "Comments used to be where leads went to die. Zapify turned them into a predictable DM pipeline—response time is basically instant now.",
        highlight: false,
      },
      {
        name: "James C.",
        role: "Coach & course brand",
        quote:
          "We kept our voice: short replies on drops, longer nurturing on FAQs, and the AI stays inside guardrails we set.",
        highlight: true,
      },
      {
        name: "Maya P.",
        role: "Beauty & skincare founder",
        quote:
          "Launch weeks used to bury our team in DMs. Automation covers the volume; we step in only for high-intent threads.",
        highlight: false,
      },
    ],
  },
  integrations: {
    title: "Plays well with the tools you already use",
    subtitle:
      "Push outcomes to CRM, billing, and docs—so marketing and rev ops see the same funnel truth without tab sprawl.",
    logos: [
      { name: "Meta", src: "/brands/meta.svg", caption: "Instagram surfaces & messaging workflows your team already trusts." },
      { name: "Zapier", src: "/brands/zapier.svg", caption: "Send leads, tags, and events to thousands of apps in one automation layer." },
      { name: "Stripe", src: "/brands/stripe.svg", caption: "Line up trials, upgrades, and paid conversions next to DM performance." },
      { name: "Notion", src: "/brands/notion.svg", caption: "Keep launch briefs and results in one hub the whole go-to-market team can read." },
    ],
  },
  faq: {
    title: "Questions teams ask before going live",
    items: [
      {
        q: "Does Zapify work with standard Instagram business accounts?",
        a: "Yes—connect the accounts you manage and run automations within Instagram’s messaging rules for compliant, user-initiated flows.",
      },
      {
        q: "Will replies feel robotic?",
        a: "You control tone, length, emoji use, and guardrails. The model adapts to each comment so answers stay contextual, not generic blasts.",
      },
      {
        q: "Is Zapify aligned with Meta / Instagram policies?",
        a: "The product is designed around user-triggered messaging and platform guidelines. Always pair automation with clear opt-in and honest offers.",
      },
      {
        q: "Can I try it before committing?",
        a: "Start with a free trial window, connect a profile, and validate DM quality with real audiences before you scale spend or headcount.",
      },
    ],
  },
  cta: {
    title: "Ready to monetize attention in the comments?",
    description:
      "Spin up your first automation in minutes—then iterate with analytics until every high-performing post has a repeatable path to revenue.",
    primary: "Get started free",
    secondary: "Talk to sales",
  },
} as const;

/** Default site accent is yellow; users can switch between Zapify, blue, orange, green, yellow, or red. */
export type AccentTheme = "zapify" | "blue" | "orange" | "green" | "yellow" | "red";
